import { Card, Modal, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { PortWharfBerthVo } from "@/entity/logistics/portWharfBerth";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { DEFAULT_VALUE } from "@/config";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import {
    HANDLING_ATTRIBUTE,
    PORT_BERTH_TYPE,
    PORT_WHARF_BERTH_GOODS_ENTERPRISE,
    PORT_WHARF_BERTH_GOODS_TYPE,
    PORT_WHARF_BERTH_TYPE,
    WATERWAY
} from "@zhihaoscm/meta";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    MultiGrid as ZhMultiGrid,
    Button
} from "zhihao-ui-react";
import { loadWharfManagementDetail } from "./WharfManagementFormPage";
import { deletePortManagement, loadPortManagementCount } from "./PortManagementDetailPage";
import { BerthLocationModal } from "./BerthLocationModal";
import { useUserStore } from "@/stores/react/authStore";

export interface WharfManagementDetailPageProps {
    wharfId?: number | string;
    loadWharf?: (id: number) => Promise<PortWharfBerthVo>;
    loadWharfCount?: (id: number, type: number) => Promise<{ dockNum: number; berthNum: number }>;
    deleteWharf?: (id: number, type: number) => Promise<unknown>;
    permissions?: string[];
}

const display = (value?: string | number | null) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : value;

/** 物流管理-码头详情，覆盖 Vue 字段枚举、显示区域、编辑入口和删除前泊位数量确认。 */
export function WharfManagementDetailPage({
    wharfId,
    loadWharf = loadWharfManagementDetail,
    loadWharfCount = loadPortManagementCount,
    deleteWharf = deletePortManagement,
    permissions
}: WharfManagementDetailPageProps) {
    const navigate = useNavigate();
    const params = useParams<{ id?: string }>();
    const [searchParams] = useSearchParams();
    const currentId = wharfId ?? params.id ?? searchParams.get("id") ?? undefined;
    const [detail, setDetail] = useState<PortWharfBerthVo>();
    const [errorMessage, setErrorMessage] = useState("");
    const [confirmMessage, setConfirmMessage] = useState("");
    const [deleting, setDeleting] = useState(false);
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 对齐 Vue v-auth：码头详情的修改、删除入口分别受 DEAL/W 权限控制。
    const currentPermissions = permissions ?? storePermissions;
    const canDeal = currentPermissions.includes("ROLE_TERMINAL_DEAL");
    const canDelete = currentPermissions.includes("ROLE_TERMINAL_W");

    useEffect(() => {
        if (!currentId) return;
        let disposed = false;
        void loadWharf(Number(currentId))
            .then(value => {
                if (!disposed) setDetail(value);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("码头详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [currentId, loadWharf]);

    const prepareDelete = async () => {
        if (!detail?.id) return;
        try {
            const count = await loadWharfCount(Number(detail.id), PORT_WHARF_BERTH_TYPE.PORT.code);
            setConfirmMessage(
                count.berthNum > 0
                    ? `码头下有${count.berthNum}个泊位，删除码头后将同时删除所有泊位。`
                    : "是否删除码头信息?"
            );
        } catch {
            setErrorMessage("码头下属数量查询失败");
        }
    };

    const confirmDelete = async () => {
        if (!detail?.id) return;
        setDeleting(true);
        try {
            await deleteWharf(Number(detail.id), PORT_WHARF_BERTH_TYPE.PORT.code);
            notifyOperationSuccess("已删除");
            setConfirmMessage("");
            navigate(-1);
        } catch {
            setErrorMessage("码头删除失败");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <section aria-label="wharf-management-detail-page" className="flex min-h-full flex-col">
            <Card>
                <ZhDetailHeader title="码头详情" />
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                {detail ? (
                    <ZhMultiGrid>
                        <ZhGrid cols={4} gap={[30, 20]}>
                            <ZhBaseItem label="名称" span={2} value={display(detail.name)} />
                            <ZhBaseItem label="简称" span={2} value={display(detail.shortName)} />
                            <ZhBaseItem label="所属港口" value={display(detail.harborShortName)} />
                            <ZhBaseItem label="面积" value={detail.area ? `${detail.area}㎡` : DEFAULT_VALUE} />
                            <ZhBaseItem label="联系人" value={display(detail.contactName)} />
                            <ZhBaseItem label="手机号" value={display(detail.phone)} />
                            <ZhBaseItem label="位置" span={4} value={display(detail.fullAddress)} />
                            <ZhBaseItem label="经纬度" span={4} value={display(detail.latLon)} />
                            <ZhBaseItem
                                label="显示区域"
                                span={4}
                                value={detail.displayArea?.length ? detail.displayArea.join(" ; ") : DEFAULT_VALUE}
                            />
                            <ZhBaseItem
                                label="码头类型"
                                value={PORT_BERTH_TYPE.getIntroByCode(detail.portBerthType) || DEFAULT_VALUE}
                            />
                            <ZhBaseItem
                                label="装卸属性"
                                value={HANDLING_ATTRIBUTE.getIntroByCode(detail.handlingAttribute) || DEFAULT_VALUE}
                            />
                            <ZhBaseItem
                                label="货物类型"
                                span={2}
                                value={PORT_WHARF_BERTH_GOODS_TYPE.getIntroByCode(detail.goodsType) || DEFAULT_VALUE}
                            />
                            <ZhBaseItem
                                label="关联企业"
                                value={
                                    PORT_WHARF_BERTH_GOODS_ENTERPRISE.getIntroByCode(detail.enterprise) || DEFAULT_VALUE
                                }
                            />
                            <ZhBaseItem
                                label="所属航道"
                                span={3}
                                value={WATERWAY.getIntroByCode(detail.waterway) || DEFAULT_VALUE}
                            />
                            <ZhBaseItem label="简介" span={4} value={display(detail.remark)} />
                        </ZhGrid>
                    </ZhMultiGrid>
                ) : null}
                <Space className="mt-6">
                    {detail && canDeal ? (
                        banButton ? (
                            <Button disabled type="link">
                                修改
                            </Button>
                        ) : (
                            <Link to={`/logistics/portWharfBerth/wharfEdit?id=${detail.id}`}>修改</Link>
                        )
                    ) : null}
                    {detail ? <BerthLocationModal berth={detail} disabled={banButton} /> : null}
                    {detail && canDelete ? (
                        <Button disabled={banButton} onClick={() => void prepareDelete()}>
                            删除
                        </Button>
                    ) : null}
                    <Button type="link" onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </Space>
            </Card>
            <Modal
                title="提示"
                open={Boolean(confirmMessage)}
                okText="确认"
                cancelText="取消"
                confirmLoading={deleting}
                onCancel={() => setConfirmMessage("")}
                onOk={() => void confirmDelete()}
            >
                {confirmMessage}
            </Modal>
        </section>
    );
}
